import { useState } from "react";
import { progressOf, useReplaceJobs } from "../lib/replaceJobs";
import { Icon } from "./icons";

// A file-set replacement after the choices are made: the same dialog the review
// ends in, showing the work that now runs app-wide (lib/replaceJobs). Hide tucks
// it into a pill; the pill, or History, brings it back.

export function ReplaceJobDialog() {
  const { shown, get, show, resume, discard, missing } = useReplaceJobs();
  const job = shown ? get(shown) : undefined;
  const [drag, setDrag] = useState(false);
  if (!job) return null;
  const { total, done, pct } = progressOf(job);
  const need = job.status === "running" ? [] : missing(job.projectId);
  const drop = (list: FileList | null) => { if (list?.length) resume(job.projectId, Array.from(list)); };
  const current = job.items.find((i) => i.name === job.now);

  return (
    <div className="rfs-scrim">
      <div className="rfs">
        <div className="rfs-head">
          <div>
            <strong>Replace file set</strong>
            <span>{job.projectName ? `${job.projectName}. ` : ""}
              {job.status === "running"
                ? "You can hide this and keep working anywhere in Mockingbird; it carries on in this tab."
                : job.status === "stopped"
                  ? "Stopped part-way. Everything that is in has been filed in place."
                  : "Paused when the tab closed. Everything that is in has been filed in place."}
            </span>
          </div>
        </div>

        <div className="rfs-body">
          <div className="rfs-progress">
            <div className="rfs-bar"><span style={{ width: `${pct}%` }} /></div>
            <div className="rfs-prog-text">
              <span>{job.status !== "running" ? `${done} of ${total} file${total === 1 ? "" : "s"} brought in`
                : job.step === "filing" ? "Filing it in place of the file it replaces"
                  : "Bringing in the files you accepted"}</span>
              <span className="mono">{done} / {total}</span>
            </div>
            {job.status === "running" && job.now && (
              <div className="rfs-prog-file mono">
                {job.now}{job.step === "filing" && current?.current ? ` → ${current.current}` : ""}
              </div>
            )}
          </div>

          {job.status === "stopped" && (
            <p className="rj-msg bad">{job.error} Try again to finish; nothing is sent twice.</p>
          )}
          {job.status !== "running" && need.length > 0 && (
            <>
              <p className="rj-msg">
                The browser can only send files you hand it, so drop the same {need.length === 1 ? "file" : `${need.length} files`} again
                to carry on. Your choices are kept.
              </p>
              <div className={`dropzone${drag ? " drag" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); drop(e.dataTransfer.files); }}
                onClick={() => document.getElementById("rj-input")?.click()}>
                <input id="rj-input" type="file" multiple accept="audio/*" hidden
                  onChange={(e) => { drop(e.target.files); e.currentTarget.value = ""; }} />
                <strong>Drop the same files here</strong>
                <div className="hint mono">
                  {need.slice(0, 4).join(", ")}{need.length > 4 ? ` and ${need.length - 4} more` : ""}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="rfs-foot">
          <div className="rfs-foot-text">
            <em>{job.status === "running"
              ? "Closing the tab pauses it; the choices are remembered."
              : "Discarding keeps every file that is already in, and leaves the rest as they were."}</em>
          </div>
          <div className="rfs-actions">
            <button className="btn" onClick={() => show(null)}>Hide</button>
            {job.status === "running" && (
              <button className="btn primary" disabled>{job.step === "filing" ? "Filing…" : "Uploading…"}</button>
            )}
            {job.status !== "running" && (
              <button className="btn" onClick={() => discard(job.projectId)}>Discard the rest</button>
            )}
            {job.status === "stopped" && (
              <button className="btn primary" onClick={() => resume(job.projectId)}>Try again</button>
            )}
            {/* Everything left is already uploaded and only needs filing. */}
            {job.status === "paused" && need.length === 0 && (
              <button className="btn primary" onClick={() => resume(job.projectId)}>Carry on</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The replacements tucked away: one small pill each, until it's done. */
export function ReplaceJobPills() {
  const { jobs, shown, show } = useReplaceJobs();
  const list = jobs.filter((j) => j.projectId !== shown);
  if (!list.length) return null;
  return (
    <div className="rj-pills">
      {list.map((j) => {
        const { total, done, pct } = progressOf(j);
        return (
          <button key={j.projectId} type="button" className={`rj-pill ${j.status}`}
            onClick={() => show(j.projectId)} title="Show the file-set replacement">
            {j.status === "running"
              ? <span className="spinner" style={{ width: 12, height: 12 }} />
              : <Icon name={j.status === "stopped" ? "alert" : "pause"} size={13} />}
            <span className="rj-pill-t">
              <b>{j.status === "running" ? "Replacing files" : j.status === "stopped" ? "Replacement stopped" : "Replacement paused"}</b>
              {j.projectName && <span>{j.projectName}</span>}
            </span>
            <span className="rj-pill-n mono">{done}/{total}</span>
            {j.status === "running" && <span className="rj-pill-bar"><i style={{ width: `${pct}%` }} /></span>}
          </button>
        );
      })}
    </div>
  );
}
